import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

import { deleteInvoice, listInvoices, markInvoicePaid } from "@/features/ops/api/opsApi";
import { useOpsStore } from "@/features/ops/model/opsStore";
import type { Invoice, InvoiceStatus } from "@/features/ops/types";
import { InvoiceDetailModal } from "@/features/ops/ui/InvoiceDetailModal";
import { Badge, Button, ConfirmDialog, type BadgeProps } from "@/shared/ui";

const STATUS_VARIANT: Record<InvoiceStatus, BadgeProps["variant"]> = {
  GENERATED: "warning",
  SENT: "info",
  PAID: "success",
};

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  GENERATED: "Draft",
  SENT: "Sent",
  PAID: "Paid",
};

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function InvoiceList() {
  const token = useOpsStore((s) => s.token)!;
  const qc = useQueryClient();
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Invoice | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["ops", "invoices"],
    queryFn: () => listInvoices(token),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteInvoice(token, id),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ["ops", "invoices"] });
      qc.invalidateQueries({ queryKey: ["invoices", "me"] });
    },
  });

  const paidMutation = useMutation({
    mutationFn: (id: string) => markInvoicePaid(token, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ops", "invoices"] });
      // Venue reads its own invoices under ["invoices", "me"] — flush so
      // the venue side sees the PAID status without a manual refresh.
      qc.invalidateQueries({ queryKey: ["invoices", "me"] });
    },
  });

  // Track which row(s) have an in-flight Mark-paid call so the disabled state
  // is scoped to the clicked row rather than every Mark-paid button.
  const [pendingPaidIds, setPendingPaidIds] = useState<Set<string>>(new Set());
  const handleMarkPaid = (id: string) => {
    setPendingPaidIds((s) => new Set(s).add(id));
    paidMutation.mutate(id, {
      onSettled: () =>
        setPendingPaidIds((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        }),
    });
  };

  if (isLoading) return <div className="text-sm text-copy-muted">Loading…</div>;
  if (error) return <div className="text-sm text-destructive">Could not load invoices.</div>;

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold tracking-display-tight text-copy">Invoices</h2>
          <div className="relative group">
            <button
              type="button"
              aria-label="How invoice amounts work"
              className="flex h-4 w-4 items-center justify-center rounded-full border border-border-strong text-[10px] font-medium leading-none text-copy-muted hover:border-copy hover:text-copy"
            >
              i
            </button>
            <div className="pointer-events-none invisible absolute left-6 top-0 z-10 w-80 rounded-md border border-border bg-surface-raised p-3 text-xs text-copy opacity-0 shadow-elevated transition-opacity group-hover:visible group-hover:opacity-100">
              <p className="mb-1 font-semibold">How invoicing works</p>
              <ul className="mb-2 space-y-1 text-copy-muted">
                <li>
                  <strong className="text-copy">Venue pays</strong> OneShift the total.
                </li>
                <li>
                  OneShift passes <strong className="text-copy">Worker pay</strong> to the worker
                  (separate Payouts tab).
                </li>
                <li>
                  <strong className="text-copy">OneShift fee</strong> (per-venue markup) stays with
                  the platform.
                </li>
              </ul>
              <p className="mb-1 font-semibold">Status flow</p>
              <ul className="space-y-1 text-copy-muted">
                <li>
                  <strong className="text-copy">Draft</strong> — auto-generated Monday 02:00, not yet emailed.
                </li>
                <li>
                  <strong className="text-copy">Mark sent</strong> — emails the invoice to the venue.
                </li>
                <li>
                  <strong className="text-copy">Mark paid</strong> — when the venue pays OneShift.
                </li>
              </ul>
            </div>
          </div>
        </div>
        <p className="text-xs text-copy-muted">
          Auto-generated every Monday at 02:00 for the prior week's completed shifts. Flow: Draft →
          Sent → Paid.
        </p>
      </div>
      {data && data.length === 0 && (
        <div className="rounded-lg border border-border bg-surface p-10 text-center text-sm text-copy-muted shadow-card">
          No invoices generated yet. Weekly invoices run Mondays at 02:00.
        </div>
      )}
      {data && data.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-card">
         <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="border-b border-border bg-surface-sunken text-left text-xs uppercase tracking-wider text-copy-muted">
              <tr>
                <th className="px-4 py-3 font-medium">Venue</th>
                <th className="px-4 py-3 font-medium">Period</th>
                <th
                  className="px-4 py-3 text-right font-medium"
                  title="What the worker gets paid (sum of completed shifts in the period)"
                >
                  Worker pay
                </th>
                <th
                  className="px-4 py-3 text-right font-medium"
                  title="OneShift's service fee on top of worker pay (per-venue markup %)"
                >
                  OneShift fee
                </th>
                <th
                  className="px-4 py-3 text-right font-medium"
                  title="What the venue pays OneShift (Worker pay + OneShift fee)"
                >
                  Venue pays
                </th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Next action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((invoice) => {
                const fee = (
                  parseFloat(invoice.total_charge) - parseFloat(invoice.total_worker_pay)
                ).toFixed(2);
                return (
                  <tr
                    key={invoice.id}
                    className="cursor-pointer transition-colors hover:bg-surface-sunken/40"
                    onClick={() => setOpenInvoiceId(invoice.id)}
                  >
                    <td className="px-4 py-3 font-medium text-copy">{invoice.venue_name}</td>
                    <td className="px-4 py-3 text-xs text-copy-muted">
                      {new Date(invoice.period_start).toLocaleDateString()} –{" "}
                      {new Date(invoice.period_end).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-copy">
                      ${invoice.total_worker_pay}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-copy">
                      <div>${fee}</div>
                      <div className="text-[10px] text-copy-subtle">
                        {invoice.markup_percentage}% markup
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums text-copy">
                      ${invoice.total_charge}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_VARIANT[invoice.status]} dot>
                        {STATUS_LABELS[invoice.status]}
                      </Badge>
                      {invoice.status === "SENT" && invoice.sent_at && (
                        <div className="mt-1 text-[10px] text-copy-subtle">
                          Sent {fmtDate(invoice.sent_at)}
                        </div>
                      )}
                      {invoice.status === "PAID" && invoice.paid_at && (
                        <div className="mt-1 text-[10px] text-copy-subtle">
                          Paid {fmtDate(invoice.paid_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        {invoice.status === "GENERATED" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setOpenInvoiceId(invoice.id)}
                          >
                            Review & send
                          </Button>
                        )}
                        {invoice.status === "SENT" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkPaid(invoice.id)}
                            disabled={pendingPaidIds.has(invoice.id)}
                          >
                            {pendingPaidIds.has(invoice.id) ? "Marking…" : "Mark paid"}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="icon-sm"
                          aria-label="Delete invoice"
                          onClick={() => setConfirmDelete(invoice)}
                          className="text-destructive-foreground hover:bg-destructive-light"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
         </div>
        </div>
      )}

      {openInvoiceId && (
        <InvoiceDetailModal invoiceId={openInvoiceId} onClose={() => setOpenInvoiceId(null)} />
      )}

      <ConfirmDialog
        open={confirmDelete !== null}
        destructive
        title="Delete this invoice?"
        description={
          <>
            This permanently deletes the invoice for{" "}
            <span className="font-medium text-copy">{confirmDelete?.venue_name}</span>{" "}
            ({confirmDelete && new Date(confirmDelete.period_start).toLocaleDateString()} –{" "}
            {confirmDelete && new Date(confirmDelete.period_end).toLocaleDateString()}). The
            underlying shifts are not affected. This cannot be undone.
          </>
        }
        confirmLabel="Delete permanently"
        loadingLabel="Deleting…"
        loading={remove.isPending}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
